import { Component, OnInit, Inject, Input } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { IOrganizationDetails, IOrganizationDetailsObject, OrganizationService } from 'src/app/services/organization.service'; 
import { Dropdown } from 'src/app/shared/models/dropdown'; 
import { Observable, map, catchError, of } from 'rxjs';
import { ReportSettingsService } from '../../services/report-settings.service';
import { LocationPreviewDialogComponent } from './location-preview-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { UserService } from 'src/app/services/user.service';
import { ConfirmationDialogComponent, IConfirmationDialogData } from 'src/app/modules/confirmation-dialog/confirmation-dialog.component';

@Component({
  selector: 'app-import-locations-dialog',
  templateUrl: './import-locations-dialog.component.html',
  styleUrls: ['./import-locations-dialog.component.css']
})
export class ImportLocationsDialogComponent implements OnInit {
  @Input() organizationId: number;
  @Input() organizationName: string;
  [x: string]: any;
  importForm: FormGroup;
  file: File | null = null;
  fileDate: Date | null = null;
  billableOrgs: Dropdown<number>[] = [];
  organization: IOrganizationDetailsObject;
  orgList = [];
  changeDetectorRef: any;
  getDataCompleted: any;
  timeZoneOptions$: Observable<Dropdown<number>[]>;
  billableOrgId: number;
  isFileSelected = false;
  previewData: any[] = []; 
  displayedColumns: string[] = [
    'select',
    'lineNumber',
    'uniqueLocationId',
    'locationName',
    'organizationName',
    'systemLocationId',
    'createdBy',
    'createdOn',
    'modifiedBy',
    'modifiedOn',
    'active'
  ];

  private currentUserId = 0;
  private currentUserName = '';

  constructor(
    public dialogRef: MatDialogRef<ImportLocationsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private fb: FormBuilder,
    private http: HttpClient,
    private organizationService: OrganizationService,
    private ReportSettingsService: ReportSettingsService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private userService: UserService
  ) {
    try {
      this.importForm = this.fb.group({
        organizationName: ['', Validators.required],
        billableOrg: [{ value: data?.organizationName && data?.billableOrgId ? `${data.organizationName} - ${data.billableOrgId}` : data?.organizationName, disabled: true }],
        timeZone: ['', Validators.required],
        file: ['', Validators.required],
        fileContent: [null]
      });
    } catch (error) {
      console.error('Error initializing dialog:', error);
      this.dialogRef.close();
      throw error;
    }
  }
    
  async ngOnInit(): Promise<void> {
    try {
      const billableOrgId = this.data?.billableOrgId;
      if (!billableOrgId) {
        this.showErrorMessage('Error: Organization ID was not provided');
        this.dialogRef.close(false);
        return;
      }

      try {
        const profile: any = await this.userService.getMyAccount().toPromise();
        this.currentUserId = profile?.user?.userId ?? profile?.userId ?? 0;
        this.currentUserName = profile?.user?.userName ?? profile?.userName ?? '';
      } catch { /* ignore */ }

      this.getOrgsByBillableOrg(billableOrgId);
      this.loadTimeZones();
    } catch (error: any) {
      this.showErrorMessage('Error initializing dialog: ' + error.message);
      this.dialogRef.close(false);
    }
  }
  
  onImportClick(): void {
    if (this.importForm.valid && this.file) {
      if (this.previewData.length > 0) {
        this.openPreviewDialog();
      } else {
        this.validateAndProcessFile(this.file);
      }
    }
  }

  onCancelClick(): void {
    this.dialogRef.close(false); 
  }

  loadTimeZones(): void {
    this.timeZoneOptions$ = this.ReportSettingsService.getTimezones(); // Assign observable
  }

  isDragging = false;
  selectedFileName: string | null = null;

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.handleFileSelection(files[0]);
    }
  }

  onFileSelected(event: any): void {
    const files = event.target.files;
    if (files && files.length > 0) {
      this.file = files[0];
      this.selectedFileName = this.file.name;
      this.fileDate = new Date(this.file.lastModified);
      this.importForm.patchValue({ file: this.file });
      this.isFileSelected = true;
      this.previewData = [];
    }
  }

  removeSelectedFile(): void {
    this.file = null;
    this.selectedFileName = null;
    this.fileDate = null;
    this.importForm.patchValue({ file: null });
    this.isFileSelected = false;
    const input = document.querySelector('input[type="file"]') as HTMLInputElement;
    if (input) {
      input.value = '';
    }
  }

  getOrgsByBillableOrg(billableOrgId: number) {
    this.orgList = []; // remove all items
    this.organizationService
      .getOrganizationDropDownByBillableOrg(billableOrgId)
      .subscribe({
        next: (data) => {
          // Format text as "OrganizationName - OrganizationId"
          this.orgList = (data || []).map((org: any) => ({
            ...org,
            text: this.normalizeOrgText(org.text, org.value)
          }));
        },
        complete: () => {
          this.getDataCompleted;
        },
      });
  }  
 
  private handleFileSelection(file: File): void {
    this.file = file;
    this.selectedFileName = file.name;
    this.fileDate = new Date(file.lastModified);
    this.importForm.patchValue({ file: file });
  }

  validateAndProcessFile(file: File): void {
    const reader = new FileReader();
    reader.onload = async (e: any) => {
      try {
        const XLSX = await import('xlsx');
        const workbook = XLSX.read(e.target.result, { type: 'binary' });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        const validation = this.validateFileStructure(jsonData);
        if (!validation.ok) {
          const validationError = validation.error || 'Invalid File Format';
          const fileHeaders = (jsonData?.[0] as any[] | undefined)?.map(h => String(h ?? '').trim()) || [];
          this.previewData = [];
          this.openValidationFailureDialog(validationError, fileHeaders);
          return;
        }

        this.processFileData(jsonData);
        if (this.previewData.length === 0) { this.showErrorMessage('No valid rows found to process'); return; }
        this.openPreviewDialog();
      } catch (error:any) { this.showErrorMessage('Error processing file: ' + error.message); }
    };
    reader.onerror = () => this.showErrorMessage('Error reading file');
    reader.readAsBinaryString(file);
  }

  private validateFileStructure(data: any[]): { ok: boolean; error?: string } {
    if (!data || data.length < 2) return { ok: false, error: 'Invalid format: empty file or missing data rows' };

    const headersRaw = data[0] as any[];
    const headers = headersRaw.map(h => String(h ?? '').trim());

    const OPTIONAL_COLUMNS = [
      'Address2',
      'State',
      'Postal Code',
      'PostalCode',
      'User Location Number',
      'Location Match Text',
      'Destination Organization'
    ];
    const ACCEPTED = new Set([...REQUIRED_COLUMNS, ...OPTIONAL_COLUMNS]);
    const expectedMin = REQUIRED_COLUMNS.length;

    if (headers.length < expectedMin) {
      return { ok: false, error: `Invalid format: expected at least ${expectedMin} columns, found ${headers.length}` };
    }

    const missing = REQUIRED_COLUMNS.filter(col => !headers.includes(col));
    if (missing.length) return { ok: false, error: `Invalid format: missing required column(s): ${missing.join(', ')}` };

    const unknown = headers.filter(h => !ACCEPTED.has(h));
    if (unknown.length) return { ok: false, error: `Invalid format: unexpected column(s): ${unknown.join(', ')}` };

    const counts: Record<string, number> = {};
    headers.forEach(h => counts[h] = (counts[h] ?? 0) + 1);
    const duplicates = Object.keys(counts).filter(k => counts[k] > 1);
    if (duplicates.length) return { ok: false, error: `Invalid format: duplicated column(s): ${duplicates.join(', ')}` };

    return { ok: true };
  }

  private processFileData(data: any[]): void {
    const headers = (data[0] as any[]).map(h => String(h ?? '').trim());
    this.previewData = data.slice(1).map((row, index) => {
      const rowData: any = {
        lineNumber: index + 1,
        selected: true,
        isValid: this.validateRow(row, headers)
      };

      headers.forEach((header: string, colIndex: number) => {
        rowData[header] = row[colIndex] || '';
      });

      return rowData;
    });
    
    console.log('Processed data:', this.previewData.length, 'rows');
    console.log('File headers:', headers);
  }

  private processSelectedLocations(selectedLocations: any[]): void {
    console.log('Processing selected locations:', selectedLocations);
    this.dialogRef.close({ success: true, data: selectedLocations });
  }

  private showErrorMessage(message: string): void {
    this.snackBar.open(message, 'Close', {
      duration: 5000,
      horizontalPosition: 'center',
      verticalPosition: 'top'
    });
  }

  private openValidationFailureDialog(validationError: string, fileHeaders: string[]): void {
    const dialogData: IConfirmationDialogData = {
      title: 'Bulk Import Location',
      content: 'The provided file did not meet the criteria. Review the Diagnostic Report.',
      yesButtonLabel: 'Diagnostic Report',
      noButtonLabel: 'OK',
      usersChoice: false,
      showYesButton: true,
      showNoButton: true
    };

    this.dialog.open(ConfirmationDialogComponent, { data: dialogData })
      .afterClosed()
      .subscribe((downloadReport: boolean) => {
        if (downloadReport) {
          this.downloadValidationDiagnosticReport(validationError, fileHeaders)
            .catch(err => this.showErrorMessage('Error generating diagnostic report: ' + err));
        }
      });
  }

  private async downloadValidationDiagnosticReport(validationError: string, fileHeaders: string[]): Promise<void> {
    const detectedColumns = (fileHeaders || []).filter(h => !!h).join(', ');
    const diagnosticRows = [
      {
        'File Name': this.file?.name || '',
        'File Date': this.fileDate ? new Date(this.fileDate).toLocaleString() : '',
        'Detected Columns': detectedColumns,
        'Error Message': validationError
      }
    ];

    const XLSX = await import('xlsx');
    const ws = XLSX.utils.json_to_sheet(diagnosticRows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Diagnostic Report');
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
    XLSX.writeFile(wb, `bulk-location-diagnostic-${timestamp}.xlsx`);
  }

  private validateRow(row: any[], headers: string[]): boolean {
    return REQUIRED_NON_EMPTY.every(requiredCol => {
      const colIndex = headers.indexOf(requiredCol);
      if (colIndex === -1) return false;
      const value = row[colIndex];
      return value && value.toString().trim() !== '';
    });
  }

  private openPreviewDialog(): void {
    console.log('Opening preview dialog with', this.previewData.length, 'locations');

    const sampleRow = this.previewData[0];
    const fileHeaders = sampleRow
      ? Object.keys(sampleRow).filter(key => !['lineNumber', 'selected', 'isValid'].includes(key))
      : [];

    const selectedOrgId = this.importForm.get('organizationName')?.value as number;
    const selectedOrg = this.orgList.find((org: any) => org.value === selectedOrgId);
    const selectedTimeZone = this.importForm.get('timeZone')?.value as number;

    const organizationId = selectedOrg?.value ?? this.data?.organizationId;
    const organizationName = selectedOrg?.text ?? this.data?.organizationName;

    if (!this.file) {
      this.showErrorMessage('No file selected');
      return;
    }

    const dialogRef = this.dialog.open(LocationPreviewDialogComponent, {
      width: '95%',
      height: '90%',
      data: {
        locations: this.previewData,
        fileHeaders: fileHeaders,
        requiredColumns: REQUIRED_COLUMNS,
        organizationId: organizationId,
        organizationName: organizationName,
        timeZoneId: selectedTimeZone,
        fileName: this.file.name,
        fileDate: new Date(this.file.lastModified),
        userId: this.currentUserId || 0,
        userName: this.currentUserName,
        billableOrgId: this.data?.billableOrgId || 0
      },
      disableClose: true
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        console.log('Import completed successfully');
        this.dialogRef.close(true);
      }
    });
  }

  private normalizeOrgText = (text: string, orgId?: number): string => {
    if (!text) return '';
    // Remove existing " - ID" suffix if present
    const match = String(text).match(/^(.+?)\s*-\s*\d+\s*$/);
    const cleanName = (match ? match[1] : String(text)).trim();
    // Add the organizationId if provided
    return orgId ? `${cleanName} - ${orgId}` : cleanName;
  }
}

const REQUIRED_COLUMNS = [
  'Location Name',
  'Address1',
  'City',
  'Country'
];

const REQUIRED_NON_EMPTY = [
  'Location Name',
  'Address1',
  'City',
  'Country'
];
